import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, IconButton, SectionHeader } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useToggleFavorite } from "../favorites/hooks/useToggleFavorite";
import { useZorni } from "../../../hooks/useZorni";
import { useRequireAuth } from "../../../hooks/useRequireAuth";
import styles from "./ThumbnailCard.module.css";

const ThumbnailCard = ({
  items = [],
  title,
  adult,
  child,
  baby,
  onItemClick,
  onClear,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId, isAuthenticated } = useZorni();
  const requireAuth = useRequireAuth();
  const { toggleFavorite } = useToggleFavorite();

  const [tripItems, setTripItems] = useState([]);
  const listRef = useRef(null);
  const [scrollState, setScrollState] = useState({ prev: false, next: false });

  useEffect(() => {
    const list = Array.isArray(items) ? items : [];
    /* Le favori est une copie figée au moment de la visite : pour un visiteur
       non connecté il n'a pas de sens, le cœur reste vide (un clic invite à
       se connecter, comme sur les autres cartes). */
    setTripItems(
      isAuthenticated ? list : list.map((item) => ({ ...item, isFavorite: false }))
    );
  }, [items, isAuthenticated]);

  /* Les flèches ne s'affichent que si la bande déborde, et chacune se
     désactive quand on atteint le bord correspondant. */
  const updateScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollState({ prev: el.scrollLeft > 2, next: el.scrollLeft < max - 2 });
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [tripItems.length, updateScrollState]);

  const scrollBy = (direction) => {
    const el = listRef.current;
    if (!el) return;
    // Deux cartes environ, sans dépasser la largeur visible.
    const step = Math.min(460, el.clientWidth * 0.9);
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  const handleUpdateFavorite = async (item) => {
    await requireAuth(async () => {
      setTripItems((prev) =>
        prev.map((p) =>
          p.id === item.id ? { ...p, isFavorite: !p.isFavorite } : p
        )
      );

      await toggleFavorite(userId, item);
    });
  };

  const handleClick = (item) => {
    onItemClick?.(item);
    if (!item?.id || !item?.tripDateId) return;
    const params = {
      nbAdults: adult,
      nbChilds: child,
      nbBabies: baby,
    };
    const queryString = Object.entries(params)
      .filter(([, value]) => value != null)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    navigate(
      `trips/${item.id}/${item.tripDateId}${queryString ? `/?${queryString}` : ""}`
    );
  };

  if (!tripItems.length) return null;

  return (
    <section className={styles.section} aria-labelledby="recent-trips-title">
      <SectionHeader
        titleId="recent-trips-title"
        kicker={t("portal.home.recent.kicker", "Votre historique")}
        title={title || t("portal.home.recent.title", "Voyages récemment visités")}
        actions={
          <>
            {onClear && (
              <Button
                variant="ghost"
                size="small"
                label={t("portal.home.recent.clear", "Effacer")}
                onClick={onClear}
              />
            )}
            {(scrollState.prev || scrollState.next) && (
              <>
                <IconButton
                  variant="outline"
                  iconName="iconChevronLeft"
                  aria-label={t("portal.home.recent.scrollLeft", "Faire défiler vers la gauche")}
                  onClick={() => scrollBy(-1)}
                  disabled={!scrollState.prev}
                />
                <IconButton
                  variant="outline"
                  iconName="iconChevronRight"
                  aria-label={t("portal.home.recent.scrollRight", "Faire défiler vers la droite")}
                  onClick={() => scrollBy(1)}
                  disabled={!scrollState.next}
                />
              </>
            )}
          </>
        }
      />
      <div className={styles.list} ref={listRef}>
        {tripItems.map((item) => (
          <Card
            key={item.id}
            type="recent"
            item={item}
            t={t}
            onClick={() => handleClick(item)}
            onUpdateFavorite={() => handleUpdateFavorite(item)}
          />
        ))}
      </div>
    </section>
  );
};

export default ThumbnailCard;
